import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import UpgradeModal from "./UpgradeModal";
import { PLAN_CONFIGS } from "@/lib/planConfig";
import { useAuth } from "@/contexts/AuthContext";
import { trackPlanSelected } from "@/lib/behaviorTracking";

const planRoles: Record<string, string> = {
  free: "Try it free",
  pro: "For serious buyers",
  elite: "For power buyers and teams",
};

export default function PricingSection() {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { user } = useAuth();

  return (
    <section className="w-full max-w-3xl mx-auto py-8 sm:py-16 px-4 overflow-hidden">
      <div className="text-center mb-8">
        <h2 className="text-lg sm:text-xl font-display font-bold text-foreground">
          Smarter decisions. Deeper validation.
        </h2>
        <p className="text-sm text-muted-foreground font-body mt-2">
          Free gets you started. Paid plans unlock the full engine.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
        {PLAN_CONFIGS.map((plan, i) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className={`rounded-xl border p-5 flex flex-col ${
              plan.highlighted
                ? "border-primary/30 bg-gradient-to-br from-primary/8 to-card ring-1 ring-primary/15"
                : plan.tier === "elite"
                  ? "border-premium-accent/20 bg-card/60"
                  : "border-border/50 bg-card/60"
            }`}
          >
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="font-display font-semibold text-sm text-foreground">{plan.name}</h3>
              {plan.badge && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-body font-medium">{plan.badge}</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground/80 font-body mb-2">
              {planRoles[plan.tier] || plan.subline}
            </p>
            <div className="mt-1 mb-4">
              <span className="text-2xl font-display font-bold text-foreground">{plan.price}</span>
              {plan.period && <span className="text-muted-foreground font-body text-xs">{plan.period}</span>}
            </div>
            <ul className="flex flex-col gap-2 mb-5 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs font-body text-secondary-foreground">
                  <Check className="w-3 h-3 text-accent shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => {
                trackPlanSelected(plan.tier, "pricing_section", !!user);
                if (plan.tier === "free") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  setUpgradeOpen(true);
                }
              }}
              className={`py-3 rounded-lg font-medium font-body text-sm transition-all ${
                plan.highlighted
                  ? "bg-primary text-primary-foreground hover:opacity-90"
                  : "bg-secondary border border-border/50 text-secondary-foreground hover:border-primary/25"
              }`}
            >
              {plan.tier === "free" ? "Continue free" : `Get ${plan.name}`}
            </button>
          </motion.div>
        ))}
      </div>
      <p className="text-center text-[11px] text-muted-foreground/90 font-body mt-5 max-w-sm mx-auto leading-relaxed">
        Not signed in? You get 2 decisions/day as a guest.<br />
        Create a free account to save decisions and try 5 Pro-level searches.
      </p>
      <p className="text-center text-[10px] text-muted-foreground/90 font-body mt-3">
        Cancel anytime.{" "}
        <a href="/refunds" className="hover:text-muted-foreground underline">Refund Policy</a>
      </p>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </section>
  );
}
